import React, { useMemo, useState } from 'react';
import { MediumHeader } from '../../../../../ui/Header/Header';
import Divider from '../../../../../ui/Divider/Divider';
import Paragraph from '../../../../../ui/Paragraph/Paragraph';
import TextLink from '../../../../../ui/TextLink/TextLink';
import TelephoneInput from '../../TelephoneInput/TelephoneInput';
import mustache from 'mustache';
import Button, { ButtonType } from '../../../../../ui/Button/BlockButton';
import DisclaimerText from '../../DisclaimerText';
import phone from 'phone';
import { parsePhoneNumber } from 'libphonenumber-js';
import Spinner from '../../../../../ui/Spinner/Spinner';
import MustacheNestedRender from '../../../../../ui/MustacheNestedRender';
import { useSettings } from '../../../../../../hooks/useSettings';
import { templateLinkContext } from '../../common/TemplateLinkContext';
import { useTranslations } from '../../../../../../hooks/useTranslations';
import styles from './Subscribed.module.css';
import { useTheme } from '../../../../../../hooks/useTheme';
import { useGlobalSettings } from '../../../../../../hooks/useGlobalSettings';
import { useFeatureFlag } from '../../../../../../hooks/FeatureFlagsProvider';

interface CprefSubscribedProps {
  featureName?: string;
  isLandscape: boolean;
  validationError?: boolean;
  contact?: string;
  networkSubscribed: boolean;
  cprefOptedIn: boolean;
  cprefSubscribed: boolean;
  marketingSubscribed: boolean;
  showContactInput: boolean;
  subscribing: boolean;
  unsubscribing: boolean;
  narvarProfileUrl: string;
  onTelephoneInputChange: (contact: string) => void;
  onMarketingOptinSubmit: (contact: string) => void;
  onMarketingOptout: () => void;
}

const Subscribed = ({
  featureName,
  isLandscape,
  validationError = false,
  contact,
  cprefOptedIn,
  cprefSubscribed,
  marketingSubscribed,
  showContactInput,
  subscribing,
  unsubscribing,
  narvarProfileUrl,
  onTelephoneInputChange,
  onMarketingOptinSubmit,
  onMarketingOptout,
}: CprefSubscribedProps) => {
  const [userSubmittedContact, setUserSubmittedContact] = useState<
    string | undefined
  >(contact);

  const { retailerName } = useGlobalSettings();
  const { notificationCenter } = useSettings();

  const trackOnlyOptinEnabled = useFeatureFlag('track-only-optin');

  const { marketingOptInPrivacyLink, marketingOptInTermsLink } =
    notificationCenter ?? {};

  const {
    notificationCenterSublimeAutoOptinTitle,
    notificationCenterSublimeAutoOptinText,
    notificationCenterSublimeManageYourPreferences,
    notificationCenterSmsSignupButton,
    notificationCenterSmsOptoutLink,
    notificationCenterMarketingOptInText,
    notificationCenterSmsInputLabel,
    notificationCenterSmsValidationErrorMessage,
    notificationCenterSmsUpdateNumberMessage,
  } = useTranslations();

  // Temp translations
  const notificationCenterSmsMarketingOptinTitle = 'Get the Latest Updates';
  const notificationCenterSublimeConfirmHeading = 'Confirm Your Phone Number';
  const notificationCenterSublimeConfirmText =
    'Reply “Y” to the message sent to the number you provided to complete your opt-in and receive updates on all of your orders.';

  const {
    bodytextDefaultNone: bodyTextStyle,
    captiontextErrorNone: errorTextStyle,
    linksNoneDefault: linkStyle,
    linksNoneHover: linkHoverStyle,
  } = useTheme();

  const formattedPhoneNumber = useMemo(() => {
    if (!contact) return;

    const { phoneNumber: parsablePhoneNumber } = phone(contact ?? '');
    const phoneNumber = parsePhoneNumber(parsablePhoneNumber ?? '');

    return phoneNumber.formatNational();
  }, [contact]);

  const handleManagePreferencesClick = () => {
    window.open(`${narvarProfileUrl}/login?type=phone`);
  };

  const handleTelephoneInputChange = (contact: string) => {
    setUserSubmittedContact(contact);
    onTelephoneInputChange(contact);
  };

  const handleMarketingOptout = () => {
    onMarketingOptout();
  };

  const handleMarketingOptinSubmit = () => {
    if (!userSubmittedContact) return;
    onMarketingOptinSubmit(userSubmittedContact);
  };

  return (
    <div>
      {(cprefOptedIn || cprefSubscribed) && (
        <>
          <MediumHeader as="h3">
            {trackOnlyOptinEnabled && !cprefSubscribed
              ? notificationCenterSublimeConfirmHeading
              : notificationCenterSublimeAutoOptinTitle}
          </MediumHeader>
          <Paragraph>
            {trackOnlyOptinEnabled && !cprefSubscribed
              ? notificationCenterSublimeConfirmText
              : notificationCenterSublimeAutoOptinText}
          </Paragraph>
          <TextLink
            linkStyle={{
              sDefault: linkStyle,
              sHover: linkHoverStyle,
            }}
            onClick={handleManagePreferencesClick}
          >
            {notificationCenterSublimeManageYourPreferences}
          </TextLink>
        </>
      )}
      {!cprefOptedIn && !cprefSubscribed && (
        <>
          <MediumHeader as="h3">
            {notificationCenterSmsUpdateNumberMessage}
          </MediumHeader>
          <div className={styles.unsubscribeButtonContainer}>
            {unsubscribing ? (
              <Spinner />
            ) : (
              <TextLink
                linkStyle={{
                  sDefault: linkStyle,
                  sHover: linkHoverStyle,
                }}
                onClick={handleMarketingOptout}
              >
                {notificationCenterSmsOptoutLink}
              </TextLink>
            )}
          </div>
        </>
      )}
      <Divider />
      {(!marketingSubscribed || showContactInput) && (
        <>
          <MediumHeader as="h3">
            {notificationCenterSmsMarketingOptinTitle}
          </MediumHeader>
          <Paragraph>
            <MustacheNestedRender
              template={mustache.parse(
                notificationCenterMarketingOptInText || '',
              )}
              context={
                new mustache.Context({
                  retailerName,
                  privacyLink: () =>
                    templateLinkContext(
                      marketingOptInPrivacyLink || '',
                      linkStyle,
                      linkHoverStyle,
                    ),
                  termsLink: () =>
                    templateLinkContext(
                      marketingOptInTermsLink || '',
                      linkStyle,
                      linkHoverStyle,
                    ),
                })
              }
            />
          </Paragraph>
          {contact && !showContactInput ? (
            <Paragraph>
              <span style={{ color: 'gray' }}>{formattedPhoneNumber}</span>
            </Paragraph>
          ) : (
            <>
              <TelephoneInput
                label={notificationCenterSmsInputLabel ?? ''}
                title={notificationCenterSmsInputLabel ?? ''}
                centered={!isLandscape}
                error={false}
                featureName={featureName}
                disabled={subscribing}
                onChangeCallback={handleTelephoneInputChange}
                s={
                  validationError
                    ? {
                        border: `1px solid ${errorTextStyle?.color ?? ''}`,
                      }
                    : undefined
                }
              />
              {validationError && (
                <div style={{ ...bodyTextStyle, ...errorTextStyle }}>
                  {notificationCenterSmsValidationErrorMessage}
                </div>
              )}
            </>
          )}
          <div className={styles.buttonContainer}>
            {subscribing ? (
              <div className={styles.spinnerContainer}>
                <Spinner />
              </div>
            ) : (
              <Button
                big
                type={ButtonType.submit}
                onClick={handleMarketingOptinSubmit}
              >
                {notificationCenterSmsSignupButton}
              </Button>
            )}
          </div>
          <div className={styles.disclaimerContainer}>
            <DisclaimerText notificationChannel={'sms'} />
          </div>
        </>
      )}
      {marketingSubscribed && !showContactInput && (
        <>
          <MediumHeader as="h3">
            {notificationCenterSmsUpdateNumberMessage}
          </MediumHeader>
          <div className={styles.unsubscribeButtonContainer}>
            {unsubscribing ? (
              <Spinner />
            ) : (
              <TextLink
                linkStyle={{
                  sDefault: linkStyle,
                  sHover: linkHoverStyle,
                }}
                onClick={handleMarketingOptout}
              >
                {notificationCenterSmsOptoutLink}
              </TextLink>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Subscribed;
